import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { calendarDays } from '../utils/attendanceCalendar';
import './AttendanceCalendarModal.css';
import AdditionalConsumptionEditor from './AdditionalConsumptionEditor';

export default function AttendanceCalendarModal({ customer, initialDate, onClose, onChanged }) {
  const { t } = useLanguage();
  const dialog = useRef(null);
  const [month, setMonth] = useState(initialDate.slice(0, 7));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [consumptionBusy, setConsumptionBusy] = useState(false);
  const [consumptionDate, setConsumptionDate] = useState(null);

  useEffect(() => {
    const element = dialog.current;
    element.showModal();
    return () => element.close();
  }, []);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError(false);
    const [year, number] = month.split('-').map(Number);
    axios.get(`/api/attendance/customers/${customer.id}/calendar`, { params: { year, month: number } })
      .then(({ data }) => { if (active) setResult(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [customer.id, month, retry]);

  const moveMonth = (amount) => {
    const value = new Date(`${month}-01T12:00:00Z`);
    value.setUTCMonth(value.getUTCMonth() + amount);
    setConsumptionDate(null);
    setMonth(value.toISOString().slice(0, 7));
  };
  const days = result ? calendarDays(month, result.presentDates, result.today, result.joinedDate) : [];
  return (
    <dialog ref={dialog} className="attendance-calendar" aria-labelledby="attendance-calendar-title"
      onCancel={event => { if (consumptionBusy) event.preventDefault(); else onClose(); }} onClick={event => { if (event.target === dialog.current && !consumptionBusy) onClose(); }}>
      <div className="attendance-calendar-header">
        <div><h2 id="attendance-calendar-title">{customer.fullName}</h2><p>{t('Attendance calendar')}</p></div>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={consumptionBusy} autoFocus aria-label={t('Close attendance calendar')}>×</button>
      </div>
      <div className="attendance-calendar-navigation">
        <button className="btn btn-secondary" disabled={consumptionBusy || month === '0001-01'} onClick={() => moveMonth(-1)} aria-label={t('Previous month')}>←</button>
        <label>{t('Month')} <input type="month" value={month} disabled={consumptionBusy} min="0001-01" max="9999-12"
          onChange={event => { if (/^\d{4}-\d{2}$/.test(event.target.value) && event.target.validity.valid) { setConsumptionDate(null); setMonth(event.target.value); } }} /></label>
        <button className="btn btn-secondary" disabled={consumptionBusy || month === '9999-12'} onClick={() => moveMonth(1)} aria-label={t('Next month')}>→</button>
      </div>
      <p className="attendance-calendar-note">{t('Absent means no attendance was recorded for that past day. Holidays and program breaks are not tracked here. Dates use India time.')}</p>
      {error ? <div role="alert">{t('Could not load attendance.')} <button className="btn btn-secondary" onClick={() => setRetry(value => value + 1)}>{t('Retry')}</button></div>
        : !result ? <p role="status">{t('Loading attendance...')}</p> : <>
          <div className="attendance-calendar-legend">
            {['Present', 'Absent', 'Pending', 'Future', 'Not enrolled'].map(status => <span key={status} className={`attendance-day-${status.replace(' ', '-').toLowerCase()}`}>{t(status)}</span>)}
          </div>
          <p>{t('Present')}: {days.filter(day => day?.status === 'Present').length} · {t('Absent')}: {days.filter(day => day?.status === 'Absent').length}</p>
          <div className="attendance-calendar-grid" role="table" aria-label={t('Monthly attendance')}>
            <div role="row" className="attendance-calendar-week">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div role="columnheader" key={day}>{t(day)}</div>)}
            </div>
            {Array.from({ length: Math.ceil(days.length / 7) }, (_, week) => <div role="row" className="attendance-calendar-week" key={week}>
              {Array.from({ length: 7 }, (_, weekday) => {
                const day = days[week * 7 + weekday];
                return day ? <div role="cell" key={weekday} aria-label={`${day.date}: ${t(day.status)}`}
                  aria-current={day.date === result.today ? 'date' : undefined}
                  className={`attendance-calendar-day attendance-day-${day.status.replace(' ', '-').toLowerCase()}`}>
                  <strong>{day.day}</strong><span>{t(day.status)}{result.attendanceCounts?.[day.date] === 2 ? ' ×2' : ''}</span>
                  {(result.additionalConsumptions || []).some(entry => entry.date === day.date && !entry.reversed) &&
                    <span>+{result.additionalConsumptions.filter(entry => entry.date === day.date && !entry.reversed).reduce((sum, entry) => sum + entry.quantity, 0)} {t('extra shakes')}</span>}
                  {day.date <= result.today && day.status !== 'Not enrolled' && <button type="button" className="attendance-extra-button"
                    aria-label={`${t('Add consumption for')} ${day.date}`} disabled={consumptionBusy} onClick={() => setConsumptionDate(day.date)}>+</button>}
                </div> : <div role="cell" key={weekday} />;
              })}
            </div>)}
          </div>
          {consumptionDate && <AdditionalConsumptionEditor key={`${customer.id}-${consumptionDate}`}
            customerId={customer.id} date={consumptionDate} onBusy={setConsumptionBusy}
            entries={(result.additionalConsumptions || []).filter(entry => entry.date === consumptionDate)}
            onClose={() => setConsumptionDate(null)}
            onSaved={() => { setRetry(value => value + 1); onChanged?.(); }} />}
        </>}
    </dialog>
  );
}
